import { createTheme, responsiveFontSizes } from '@mui/material/styles';

/**
 * Códice Botánico — MUI theme for the app interior.
 *
 * Source of truth: DESIGN.md + frontend/src/styles/codice.css.
 * The memorable thing: "nutrición que te acompaña, sin culpa".
 *
 * Key decisions (see DESIGN.md Decisions Log, 2026-06-11):
 *  R1 — App background is warm amate paper (#F4EBD9), never clinical gray.
 *  R2 — Fraunces (apothecary serif) for h1–h4 screen titles only;
 *       Hanken Grotesk carries body/UI.
 *  R3 — Anti-guilt semantics: `warning` is cempasúchil (warm marigold),
 *       NEVER red. `error`/red (jamaica) is reserved for destructive
 *       actions and system failures — never for adherence states.
 *
 * Wrapped in responsiveFontSizes() so headings scale down on phones.
 */

const CDX = {
  paper: '#F4EBD9',
  surface: '#FBF5E9',
  paper2: '#ECDDC4',
  edge: '#D8C29A',
  ink: '#20251C',
  inkSoft: '#4C5142',
  inkFaint: '#7A7E6C',
  jade: '#1F5C3D',
  jadeDeep: '#143C28',
  jadeBright: '#2E8B57',
  agave: '#8FA37E',
  cempa: '#E0892E',
  cempaDeep: '#C26F1C',
  copal: '#B8531F',
  jamaica: '#8E2C42',
  turquesa: '#2C7D8C',
  nightText: '#EBE2CF',
};

const FONT_BODY = ['"Hanken Grotesk"', 'system-ui', 'sans-serif'].join(',');
const FONT_DISPLAY = ['Fraunces', 'Georgia', 'serif'].join(',');

let theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: CDX.jade,
      light: CDX.jadeBright,
      dark: CDX.jadeDeep,
      contrastText: CDX.nightText,
    },
    secondary: {
      main: CDX.cempa,
      light: '#EBAA5E',
      dark: CDX.cempaDeep,
      contrastText: '#2A1A06',
    },
    tertiary: {
      main: CDX.turquesa,
      light: '#5AA3B0',
      dark: '#1F5862',
      contrastText: '#FFFFFF',
    },
    // R3 — jamaica is destructive-only. Never style adherence states with it.
    error: {
      main: CDX.jamaica,
      light: '#C16077',
      dark: '#6E2233',
    },
    // R3 — warnings are warm marigold, not alarm-red.
    warning: {
      main: CDX.cempa,
      light: '#EBAA5E',
      dark: CDX.cempaDeep,
    },
    info: {
      main: CDX.turquesa,
      light: '#5AA3B0',
      dark: '#1F5862',
    },
    success: {
      main: CDX.jadeBright,
      light: '#5BA87C',
      dark: CDX.jade,
    },
    background: {
      default: CDX.paper,    // R1 — amate, not #F5F5F5
      paper: CDX.surface,    // warm card surface
      elevated: CDX.paper2,
    },
    text: {
      primary: CDX.ink,
      secondary: CDX.inkSoft,
      disabled: CDX.inkFaint,
    },
    divider: CDX.edge,
  },

  typography: {
    fontFamily: FONT_BODY,

    // R2 — Fraunces only on screen/section titles (h1–h4).
    h1: { fontFamily: FONT_DISPLAY, fontSize: '3.0rem', fontWeight: 600, lineHeight: 1.15, letterSpacing: '-0.01em' },
    h2: { fontFamily: FONT_DISPLAY, fontSize: '2.4rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.01em' },
    h3: { fontFamily: FONT_DISPLAY, fontSize: '1.9rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontFamily: FONT_DISPLAY, fontSize: '1.55rem', fontWeight: 600, lineHeight: 1.35 },
    // h5/h6 stay grotesque — card titles and UI headings, not "screen titles".
    h5: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.5 },
    h6: { fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.5 },

    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },

    button: {
      fontSize: '0.9rem',
      fontWeight: 600,
      lineHeight: 1.75,
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.66 },
    overline: {
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: '0.68rem',
      fontWeight: 500,
      lineHeight: 2.4,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
    },
  },

  // Codex radii are gentle, never pill (DESIGN.md: no bubble-radius).
  shape: {
    borderRadius: 8,
  },

  // Warm shadows — rgba on a brown base, never neutral gray.
  shadows: [
    'none',
    '0px 1px 2px rgba(40, 30, 12, 0.08)',
    '0px 2px 4px rgba(40, 30, 12, 0.09)',
    '0px 4px 8px rgba(40, 30, 12, 0.10)',
    '0px 6px 12px rgba(40, 30, 12, 0.12)',
    '0px 8px 16px rgba(40, 30, 12, 0.13)',
    '0px 12px 24px rgba(40, 30, 12, 0.14)',
    '0px 16px 32px rgba(40, 30, 12, 0.16)',
    '0px 24px 48px rgba(30, 22, 8, 0.18)',
    '0px 32px 64px rgba(30, 22, 8, 0.20)',
    '0px 40px 80px rgba(30, 22, 8, 0.22)',
    '0px 48px 96px rgba(30, 22, 8, 0.24)',
    '0px 56px 112px rgba(30, 22, 8, 0.26)',
    '0px 64px 128px rgba(30, 22, 8, 0.28)',
    '0px 72px 144px rgba(30, 22, 8, 0.30)',
    '0px 80px 160px rgba(30, 22, 8, 0.32)',
    '0px 88px 176px rgba(30, 22, 8, 0.34)',
    '0px 96px 192px rgba(30, 22, 8, 0.36)',
    '0px 104px 208px rgba(30, 22, 8, 0.38)',
    '0px 112px 224px rgba(30, 22, 8, 0.40)',
    '0px 120px 240px rgba(30, 22, 8, 0.42)',
    '0px 128px 256px rgba(30, 22, 8, 0.44)',
    '0px 136px 272px rgba(30, 22, 8, 0.46)',
    '0px 144px 288px rgba(30, 22, 8, 0.48)',
    '0px 152px 304px rgba(30, 22, 8, 0.50)',
  ],

  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      // Códice signature curve (matches codice.css interactions).
      spring: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: CDX.paper, // R1 also when CssBaseline paints body
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '9px 20px',
          fontSize: '0.9rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.18s cubic-bezier(0.2, 0.8, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(31, 92, 61, 0.28)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 6px 16px rgba(31, 92, 61, 0.32)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        },
      },
      defaultProps: {
        disableElevation: false,
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${CDX.edge}`,
          boxShadow: '0px 4px 12px rgba(40, 30, 12, 0.10)',
          transition: 'all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(40, 30, 12, 0.14)',
            transform: 'translateY(-3px)',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 8 },
        elevation1: { boxShadow: '0px 2px 4px rgba(40, 30, 12, 0.09)' },
        elevation2: { boxShadow: '0px 4px 8px rgba(40, 30, 12, 0.10)' },
        elevation3: { boxShadow: '0px 6px 12px rgba(40, 30, 12, 0.12)' },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999, // chips/tags are the one pill exception (codice .cdx-tag)
          fontWeight: 600,
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(40, 30, 12, 0.10)',
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': { borderRadius: 8 },
        },
      },
    },

    // 16px inputs on phones: below that, iOS Safari auto-zooms on focus and
    // leaves the whole app zoomed-in (the "pinch out to see the screen" bug).
    MuiInputBase: {
      styleOverrides: {
        input: {
          '@media (max-width: 600px)': {
            fontSize: 16,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 14 },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: { borderRadius: '0px 14px 14px 0px' },
      },
    },
  },

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },

  spacing: 8,

  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
});

// Headings scale down automatically on phone viewports — part of the
// mobile-adaptation pass (a 3rem h1 on a 375px screen was unusable).
theme = responsiveFontSizes(theme, { factor: 2.4 });

export default theme;
